import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Accordion, Container, Form, Row, Col, Spinner } from 'react-bootstrap';
import Page from '../../../components/layout/Page';
import { useToast } from '../../../components/ui/ToastProvider';
import { FaCheck, FaTrash, FaClipboardList, FaClock, FaUsers } from 'react-icons/fa';
import { GiDeerTrack } from 'react-icons/gi';
import './ClubRulesPage.css';
import './clubRulesFieldEdition.css';
import api from '../../../utils/api';
import { fetchClub } from '../utils/clubsCache';
import {
  sendClubAnnouncementEncrypted,
  clubParticipantIdsForBroadcast,
} from '../../../utils/messageSendE2ee';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { YButton } from '../../../ui/YButton';
// V2 enabled: force V2 on this page
import { YTable } from '../../../ui/YTable';
import { YFormField } from '../../../ui/YFormField';
import { YInput } from '../../../ui/YInput';
import { YTextarea } from '../../../ui/YTextarea';
import { listClubRuleItems } from '../clubRulesDisplayUtils';
import { stateSeasonsBlock } from '../calendar/huntSeasonCalendar';
import { isClubJoinGateActive } from '../utils/clubJoinGateUtils';
import './club-detail-shell.css';

export default function ClubRulesPage({
  embedded = false,
  joinMode = false,
  onJoinGateChange,
  onJoinComplete,
}) {
  const { clubId } = useParams();
  const { show } = useToast();
  const [club, setClub] = useState(null);
  const [rules, setRules] = useState({ version: 0, fields: {}, acknowledged: false });
  const [membersAck, setMembersAck] = useState([]);
  const [ackSummary, setAckSummary] = useState(null);
  const [ackLoading, setAckLoading] = useState(false);
  const [ackFilter, setAckFilter] = useState('all'); // all|ack|pending
  const [ackSearch, setAckSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [overview, setOverview] = useState('');
  const [huntingRegulations, setHuntingRegulations] = useState(['']);
  const [generalRules, setGeneralRules] = useState(['']);
  const [huntingHours, setHuntingHours] = useState([{ label: '', detail: '' }]);
  const [keyDates, setKeyDates] = useState([{ label: '', date: '', note: '' }]);
  const [harvest, setHarvest] = useState({
    max_deer_allowed: '',
    max_bucks_allowed: '',
    buck_min_points: '',
    buck_min_spread: '',
    buck_notes: '',
    max_does_allowed: '',
  });
  const { currentUser } = useCurrentUser();
  const [sendingIds, setSendingIds] = useState(new Set());

  const renderNumberedRuleList = (rules, emptyLabel) => {
    const items = listClubRuleItems(rules);
    if (items.length === 0) {
      return <div className="club-rules-empty text-muted">{emptyLabel}</div>;
    }
    return (
      <ol className="club-rules-numbered-list" data-testid="club-rules-numbered-list">
        {items.map((item) => (
          <li key={`${item.n}-${item.raw}`} className="club-rules-numbered-item">
            <span className="club-rules-numbered-item__n" aria-hidden="true">
              {item.n}
            </span>
            <div className="club-rules-numbered-item__copy">
              <div className="club-rules-numbered-item__title">{item.title}</div>
              {item.body ? (
                <div className="club-rules-numbered-item__body">{item.body}</div>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
    );
  };

  const renderHarvestLimitRows = () => {
    const rows = [
      ['Max deer allowed', harvest.max_deer_allowed],
      ['Max bucks allowed', harvest.max_bucks_allowed],
      ['Max does allowed', harvest.max_does_allowed],
      ['Buck minimum points', harvest.buck_min_points],
      ['Buck minimum spread (inches)', harvest.buck_min_spread],
      ['Buck notes', harvest.buck_notes],
    ].filter(([, v]) => v !== '' && v !== null && v !== undefined);

    if (rows.length === 0) {
      return <div className="club-rules-empty text-muted">No harvest limits provided.</div>;
    }

    return (
      <div className="club-rules-harvest-limits" data-testid="club-rules-harvest-limits">
        <div className="club-rules-harvest-limits__label">Harvest limits</div>
        {rows.map(([label, value]) => (
          <div key={label} className="club-rules-harvest-row">
            <span className="club-rules-harvest-row__label">{label}</span>
            <span className="club-rules-harvest-row__value">{value}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderHuntingRegulations = () => (
    isPresident ? (
      <div className="d-grid gap-3">
        <div className="d-grid gap-2">
          {huntingRegulations.map((rule, idx) => (
            <div key={idx} className="d-flex align-items-center gap-2">
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <YFormField htmlFor={`reg-${idx}`} className="flex-grow-1" style={{ marginBottom: 0 }}>
                  <YInput id={`reg-${idx}`} placeholder={`Regulation ${idx + 1}`} value={rule}
                    onChange={(e) => setHuntingRegulations(prev => prev.map((r, i) => i === idx ? e.target.value : r))}
                  />
                </YFormField>
              </div>
              <YButton variant="ghost" onClick={() => handleRemoveHuntingReg(idx)} title="Delete regulation" aria-label="Delete regulation" className="d-inline-flex align-items-center justify-content-center y-btn-icon" style={{ color: 'var(--color-danger)' }}>
                <FaTrash />
              </YButton>
            </div>
          ))}
          <div>
            <YButton variant="secondary" onClick={handleAddHuntingReg}>Add Regulation</YButton>
          </div>
        </div>

        <div className="border-top pt-2">
          <h6 className="mb-2">Harvest Limits</h6>
          <Row className="mb-1">
            <Col md={4} className="mb-2">
              <YFormField label="Max deer allowed" htmlFor="max-deer">
                <YInput id="max-deer" type="number" min={0} value={harvest.max_deer_allowed}
                  onChange={(e) => setHarvest(h => ({ ...h, max_deer_allowed: e.target.value }))}
                  disabled={!isPresident}
                />
              </YFormField>
            </Col>
            <Col md={4} className="mb-2">
              <YFormField label="Max bucks allowed" htmlFor="max-bucks">
                <YInput id="max-bucks" type="number" min={0} value={harvest.max_bucks_allowed}
                  onChange={(e) => setHarvest(h => ({ ...h, max_bucks_allowed: e.target.value }))}
                  disabled={!isPresident}
                />
              </YFormField>
            </Col>
            <Col md={4} className="mb-2">
              <YFormField label="Max does allowed" htmlFor="max-does">
                <YInput id="max-does" type="number" min={0} value={harvest.max_does_allowed}
                  onChange={(e) => setHarvest(h => ({ ...h, max_does_allowed: e.target.value }))}
                  disabled={!isPresident}
                />
              </YFormField>
            </Col>
          </Row>
          <Row>
            <Col md={4} className="mb-2">
              <YFormField label="Buck minimum points" htmlFor="buck-points">
                <YInput id="buck-points" type="number" min={0} value={harvest.buck_min_points}
                  onChange={(e) => setHarvest(h => ({ ...h, buck_min_points: e.target.value }))}
                  disabled={!isPresident}
                />
              </YFormField>
            </Col>
            <Col md={4} className="mb-2">
              <YFormField label="Buck minimum spread (inches)" htmlFor="buck-spread">
                <YInput id="buck-spread" type="number" min={0} step={0.1} value={harvest.buck_min_spread}
                  onChange={(e) => setHarvest(h => ({ ...h, buck_min_spread: e.target.value }))}
                  disabled={!isPresident}
                />
              </YFormField>
            </Col>
            <Col md={4} className="mb-2">
              <YFormField label="Buck notes" htmlFor="buck-notes">
                <YInput id="buck-notes" type="text" value={harvest.buck_notes}
                  onChange={(e) => setHarvest(h => ({ ...h, buck_notes: e.target.value }))}
                  disabled={!isPresident}
                />
              </YFormField>
            </Col>
          </Row>
        </div>
      </div>
    ) : (
      <div className="club-rules-section-body">
        {renderNumberedRuleList(huntingRegulations, 'No hunting regulations listed.')}
        {renderHarvestLimitRows()}
      </div>
    )
  );

  const renderGeneralRules = () => (
    isPresident ? (
      <div className="d-grid gap-2">
        {generalRules.map((rule, idx) => (
          <div key={idx} className="d-flex align-items-center gap-2">
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <YFormField htmlFor={`rule-${idx}`} className="flex-grow-1" style={{ marginBottom: 0 }}>
                <YInput id={`rule-${idx}`} placeholder={`Rule ${idx + 1}`} value={rule}
                  onChange={(e) => setGeneralRules(prev => prev.map((r, i) => i === idx ? e.target.value : r))}
                />
              </YFormField>
            </div>
            <YButton variant="ghost" onClick={() => handleRemoveGeneral(idx)} title="Delete rule" aria-label="Delete rule" className="d-inline-flex align-items-center justify-content-center y-btn-icon" style={{ color: 'var(--color-danger)' }}>
              <FaTrash />
            </YButton>
          </div>
        ))}
        <div>
          <YButton variant="secondary" onClick={handleAddGeneral}>Add Rule</YButton>
        </div>
      </div>
    ) : (
      renderNumberedRuleList(generalRules, 'No general rules listed.')
    )
  );

  const renderStateSeasons = () => {
    if (!stateSeasons.visible) return null;
    return (
      <div className="club-rules-state-seasons" data-testid="club-rules-state-seasons">
        <div className="club-rules-state-seasons__kicker">State deer seasons</div>
        <div className="club-rules-state-seasons__title">{stateSeasons.title}</div>
        <table className="club-rules-state-seasons__table">
          <thead>
            <tr>
              <th>Season</th>
              <th>Sex</th>
              <th>Dates</th>
            </tr>
          </thead>
          <tbody>
            {stateSeasons.rows.map((row) => (
              <tr key={`${row.kind}-${row.dates}-${row.sexLabel}`}>
                <td>{row.kindLabel}</td>
                <td>{row.sexLabel}</td>
                <td>{row.dates}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="club-rules-state-seasons__note">
          {stateSeasons.disclaimer}
          {stateSeasons.officialUrl ? (
            <>
              {' '}
              <a href={stateSeasons.officialUrl} target="_blank" rel="noreferrer">
                Outdoor Alabama
              </a>
            </>
          ) : null}
        </p>
      </div>
    );
  };

  const renderHuntingHours = () => (
    isPresident ? (
      <div className="d-grid gap-2">
        {huntingHours.map((row, idx) => (
          <div key={idx} className="d-flex flex-column flex-md-row gap-2">
            <YFormField htmlFor={`hh-label-${idx}`} className="flex-grow-1" style={{ marginBottom: 0 }}>
              <YInput id={`hh-label-${idx}`} placeholder="Label (e.g., Rifle)" value={row.label || ''}
                onChange={(e) => setHuntingHours(prev => prev.map((r, i) => i === idx ? { ...r, label: e.target.value } : r))}
              />
            </YFormField>
            <YFormField htmlFor={`hh-detail-${idx}`} className="flex-grow-1" style={{ marginBottom: 0 }}>
              <YInput id={`hh-detail-${idx}`} placeholder="Dates/Hours" value={row.detail || ''}
                onChange={(e) => setHuntingHours(prev => prev.map((r, i) => i === idx ? { ...r, detail: e.target.value } : r))}
              />
            </YFormField>
            <YButton variant="ghost" onClick={() => handleRemoveHuntingHour(idx)} title="Delete entry" aria-label="Delete entry" className="d-inline-flex align-items-center justify-content-center y-btn-icon" style={{ color: 'var(--color-danger)' }}>
              <FaTrash />
            </YButton>
          </div>
        ))}
        <div>
          <YButton variant="secondary" onClick={handleAddHuntingHour}>Add Entry</YButton>
        </div>
      </div>
    ) : (
      <div className="club-rules-hours-list" data-testid="club-rules-hours-list">
        {(huntingHours || []).filter((r) => r && (r.label || r.detail)).length ? (
          huntingHours
            .filter((r) => r && (r.label || r.detail))
            .map((r, idx) => (
              <div key={idx} className="club-rules-hours-row">
                <div className="club-rules-hours-row__label">{r.label || 'Hours'}</div>
                {r.detail ? <div className="club-rules-hours-row__detail">{r.detail}</div> : null}
              </div>
            ))
        ) : (
          <div className="club-rules-empty text-muted">No hours or seasons listed.</div>
        )}
      </div>
    )
  );

  const loadClub = useCallback(async () => {
    try {
      const resp = await fetchClub(api, clubId);
      setClub(resp.data);
    } catch (e) {}
  }, [clubId]);

  const loadRules = useCallback(async () => {
    try {
      const resp = await api.get(`/clubs/${clubId}/rules/`);
      setRules(resp.data);
      if (onJoinGateChange) {
        onJoinGateChange(
          isClubJoinGateActive({
            isPresident: Boolean(club?.is_president),
            acknowledged: resp.data?.acknowledged,
          })
        );
      }
      // If president, load member acknowledgements in a follow-up call
      if (club?.is_president) {        setAckLoading(true);
        try {
          const resp2 = await api.get(`/clubs/${clubId}/rules/?include_members=1`);
          setMembersAck(resp2.data.members_ack || []);
          setAckSummary(resp2.data.summary || null);
        } finally {
          setAckLoading(false);
        }
      } else {
        setMembersAck([]);
        setAckSummary(null);
      }
      const f = resp.data.fields || {};
      setOverview(f.overview || '');
      setHuntingRegulations(Array.isArray(f.hunting_regulations) && f.hunting_regulations.length ? f.hunting_regulations : ['']);
      const generalSource = Array.isArray(f.general_rules) ? f.general_rules : (Array.isArray(f.general) ? f.general : []);
      setGeneralRules(generalSource.length ? generalSource : ['']);
      setHuntingHours(Array.isArray(f.hunting_hours_seasons) && f.hunting_hours_seasons.length ? f.hunting_hours_seasons : [{ label: '', detail: '' }]);
      setKeyDates(Array.isArray(f.key_dates) && f.key_dates.length ? f.key_dates : [{ label: '', date: '', note: '' }]);
      setHarvest({
        max_deer_allowed: f.harvest?.max_deer_allowed ?? '',
        max_bucks_allowed: f.harvest?.max_bucks_allowed ?? '',
        buck_min_points: f.harvest?.buck_min_points ?? '',
        buck_min_spread: f.harvest?.buck_min_spread ?? '',
        buck_notes: f.harvest?.buck_notes ?? '',
        max_does_allowed: f.harvest?.max_does_allowed ?? '',
      });
    } catch (e) {}
  }, [clubId, club?.is_president, onJoinGateChange]);

  useEffect(() => {
    loadClub();
  }, [loadClub]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const handleAddGeneral = () => {
    setGeneralRules((prev) => [...prev, '']);
  };
  const handleRemoveGeneral = (idx) => {
    setGeneralRules((prev) => prev.filter((_, i) => i !== idx).length ? prev.filter((_, i) => i !== idx) : ['']);
  };

  const handleAddHuntingReg = () => setHuntingRegulations((prev) => [...prev, '']);
  const handleRemoveHuntingReg = (idx) => setHuntingRegulations((prev) => prev.filter((_, i) => i !== idx).length ? prev.filter((_, i) => i !== idx) : ['']);

  const handleAddHuntingHour = () => setHuntingHours((prev) => [...prev, { label: '', detail: '' }]);
  const handleRemoveHuntingHour = (idx) => setHuntingHours((prev) => prev.filter((_, i) => i !== idx).length ? prev.filter((_, i) => i !== idx) : [{ label: '', detail: '' }]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const cleanStringList = (arr) => (arr || []).map(r => (r || '').trim()).filter(Boolean);
      const cleanObjList = (arr, requiredKeys, optionalKeys = []) => {
        const safe = Array.isArray(arr) ? arr : [];
        return safe
          .map((item) => {
            const obj = { ...(item || {}) };
            const cleaned = {};
            requiredKeys.forEach(k => { cleaned[k] = (obj[k] || '').trim(); });
            optionalKeys.forEach(k => { cleaned[k] = (obj[k] || '').trim(); });
            return cleaned;
          })
          .filter((item) => requiredKeys.some(k => item[k]));
      };
      const fields = {
        overview: (overview || '').trim(),
        hunting_regulations: cleanStringList(huntingRegulations),
        general_rules: cleanStringList(generalRules),
        hunting_hours_seasons: cleanObjList(huntingHours, ['label', 'detail']),
        key_dates: cleanObjList(keyDates, ['label'], ['date', 'note']),
        harvest: {
          max_deer_allowed: toIntOrNull(harvest.max_deer_allowed),
          max_bucks_allowed: toIntOrNull(harvest.max_bucks_allowed),
          buck_min_points: toIntOrNull(harvest.buck_min_points),
          buck_min_spread: toFloatOrNull(harvest.buck_min_spread),
          buck_notes: (harvest.buck_notes || '').trim(),
          max_does_allowed: toIntOrNull(harvest.max_does_allowed),
        },
      };
      const resp = await api.patch(`/clubs/${clubId}/rules/`, { fields });
      setRules(resp.data);
      show({ title: 'Club rules updated', body: 'Club rule update sent for acknowledgement.', variant: 'success' });
    } catch (e) {
      alert('Failed to save rules');
    } finally {
      setSaving(false);
    }
  };

  const handleAcknowledge = async () => {
    try {
      await api.post(`/clubs/${clubId}/rules/ack/`, {});
      await loadRules();
      onJoinGateChange?.(false);
      if (joinMode) {
        try {
          show({
            title: 'Welcome to the club',
            body: 'Thanks for acknowledging the rules. You’re ready to hunt with your crew.',
            variant: 'success',
          });
        } catch (_) { /* no-op */ }
        onJoinComplete?.();
      }
    } catch (e) {}
  };

  const sendReminderDM = async (member) => {
    try {
      setSendingIds(prev => {
        const s = new Set(prev);
        s.add(member.member_id);
        return s;
      });
      // Build reminder content consistent with club update message
      const fieldKeys = Object.keys(rules.fields || {}).filter(Boolean);
      const fieldsText = fieldKeys.length ? fieldKeys.join(', ') : '—';
      const content = `Club rules updated to v${rules.version} (fields: ${fieldsText}). Please review and acknowledge: [Open Rules](/clubs/${clubId}/rules)`;
      // Send as a club announcement to all members
      await sendClubAnnouncementEncrypted(api, {
        clubId,
        participantUserIds: clubParticipantIdsForBroadcast(club),
        plaintext: content,
      });
      alert('Reminder sent as a club announcement.');
    } catch (e) {
      // Optional: surface lightweight feedback
      console.error('Failed to send reminder', e);
      alert('Failed to send reminder.');
    } finally {
      setSendingIds(prev => {
        const s = new Set(prev);
        s.delete(member.member_id);
        return s;
      });
    }
  };

  const isPresident = !!club?.is_president;
  const stateSeasons = useMemo(
    () => stateSeasonsBlock(club?.hunt_zone, club?.hunt_seasons),
    [club]
  );

  const filteredAckRows = useMemo(() => {
    const q = ackSearch.trim().toLowerCase();
    return membersAck
      .filter((r) => !currentUser || r.member_id !== currentUser.id)
      .filter((r) => {
        if (ackFilter === 'ack') return r.acknowledged;
        if (ackFilter === 'pending') return !r.acknowledged;
        return true;
      })
      .filter((r) => {
        if (!q) return true;
        const name = `${r.first_name || ''} ${r.last_name || ''}`.trim().toLowerCase();
        return (r.username || '').toLowerCase().includes(q) || name.includes(q);
      });
  }, [membersAck, ackFilter, ackSearch, currentUser]);

  const rulesMain = (
    <>
      {!isPresident && rules.acknowledged === false ? (
        <div className="club-rules-join-banner mb-4" role="status" data-testid="club-rules-join-banner">
          <strong>Finish joining</strong>
          <p className="mb-0">
            Welcome to {club?.name || 'your club'}. Read the rules below, then acknowledge them to finish joining.
            The rest of the club stays locked until you do.
          </p>
        </div>
      ) : null}
      <div className="club-detail-section club-rules-intro mb-4" data-testid="club-rules-intro">
        <div className="club-detail-section__kicker">
          <FaClipboardList aria-hidden />
          <span>Club rules</span>
        </div>
        <h2 className="club-rules-title h5 mb-1">{club?.name || 'Club'}</h2>
        <div className="club-rules-intro__meta">
          <p className="club-rules-version mb-0">Version {rules.version}</p>
          <span
            className={`club-rules-ack-status club-rules-ack-status--${rules.acknowledged ? 'ok' : 'pending'}`}
            data-testid="club-rules-ack-status"
          >
            {rules.acknowledged ? 'Acknowledged' : 'Not acknowledged'}
          </span>
        </div>
        {isPresident ? (
          <div className="mt-3">
            <YFormField label="High-level overview" htmlFor="club-rules-overview">
              <YTextarea
                id="club-rules-overview"
                rows={4}
                placeholder="Purpose of this club and how rules should be read."
                value={overview}
                onChange={(e) => setOverview(e.target.value)}
              />
            </YFormField>
          </div>
        ) : (
          <p className={`club-detail-about-lead mb-0 mt-3 ${overview ? '' : 'text-muted fst-italic'}`}>
            {overview || 'No overview provided.'}
          </p>
        )}
      </div>

      <Accordion
        alwaysOpen
        defaultActiveKey={['hunting', 'general', 'hours']}
        className="club-rules-accordion mb-4"
      >
        <Accordion.Item eventKey="hunting" className="club-rules-accordion__item">
          <Accordion.Header className="club-rules-accordion__header">
            <div className="club-detail-section__kicker mb-0">
              <GiDeerTrack aria-hidden />
              <span>Hunting regulations</span>
            </div>
          </Accordion.Header>
          <Accordion.Body className="club-rules-accordion__body">
            {renderHuntingRegulations()}
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item eventKey="general" className="club-rules-accordion__item">
          <Accordion.Header className="club-rules-accordion__header">
            <div className="club-detail-section__kicker mb-0">
              <FaClipboardList aria-hidden />
              <span>General club rules</span>
            </div>
          </Accordion.Header>
          <Accordion.Body className="club-rules-accordion__body">
            {renderGeneralRules()}
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item eventKey="hours" className="club-rules-accordion__item">
          <Accordion.Header className="club-rules-accordion__header">
            <div className="club-detail-section__kicker mb-0">
              <FaClock aria-hidden />
              <span>Hunting hours & seasons</span>
            </div>
          </Accordion.Header>
          <Accordion.Body className="club-rules-accordion__body">
            {renderStateSeasons()}
            {stateSeasons.visible ? (
              <div className="club-rules-hours-kicker">Club hours & seasons</div>
            ) : null}
            {renderHuntingHours()}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      {isPresident && (
        <div className="club-detail-section club-rules-acks mb-4" data-testid="club-rules-ack-card">
          <div className="club-detail-section__kicker">
            <FaUsers aria-hidden />
            <span>Acknowledgements</span>
          </div>
          {ackSummary ? (
            <p className="club-rules-version mb-3">
              Acknowledged {ackSummary.acknowledged_count}/{ackSummary.total_members} · Pending {ackSummary.pending_count} · v{rules.version}
            </p>
          ) : (
            <p className="club-detail-section__subhead mb-3">Member acknowledgement status</p>
          )}
          <div className="club-rules-ack-toolbar d-flex flex-wrap gap-2 align-items-center mb-3">
            <Form.Select
              className="club-rules-select"
              aria-label="Filter acknowledgements"
              value={ackFilter}
              onChange={(e) => setAckFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="ack">Acknowledged</option>
              <option value="pending">Pending</option>
            </Form.Select>
            <YInput
              type="search"
              className="club-rules-ack-search"
              placeholder="Search member"
              aria-label="Search members in acknowledgement list"
              value={ackSearch}
              onChange={(e) => setAckSearch(e.target.value)}
            />
          </div>
          {ackLoading ? (
            <div className="text-center py-3 text-muted small">
              <Spinner animation="border" size="sm" className="me-2" aria-hidden />
              Loading…
            </div>
          ) : (
            <YTable className="club-rules-ack-table mb-0">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Rules acknowledged</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredAckRows.map((r) => (
                  <tr key={r.member_id}>
                    <td>
                      <div className="fw-semibold">{r.username}</div>
                      <div className="text-muted small">{[r.first_name, r.last_name].filter(Boolean).join(' ')}</div>
                    </td>
                    <td>
                      {r.acknowledged ? (
                        <span className="club-rules-pill club-rules-pill--ok">Acknowledged</span>
                      ) : (
                        <span className="club-rules-pill club-rules-pill--pending">Pending</span>
                      )}
                    </td>
                    <td>
                      {!r.acknowledged ? (
                        <YButton
                          variant="secondary"
                          size="sm"
                          onClick={() => sendReminderDM(r)}
                          disabled={sendingIds.has(r.member_id)}
                        >
                          {sendingIds.has(r.member_id) ? 'Sending…' : 'Send reminder'}
                        </YButton>
                      ) : (
                        <span className="text-muted small">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </YTable>
          )}
        </div>
      )}

      <div className="club-rules-actions d-flex flex-wrap align-items-center gap-2 mt-2">
        {isPresident ? (
          <YButton variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving…
              </>
            ) : (
              'Save rules'
            )}
          </YButton>
        ) : !rules.acknowledged ? (
          <YButton variant="primary" onClick={handleAcknowledge}>
            <FaCheck className="me-2" aria-hidden />
            Acknowledge rules
          </YButton>
        ) : (
          <span className="club-rules-pill club-rules-pill--ok">
            <FaCheck className="me-1" aria-hidden />
            Acknowledged
          </span>
        )}
      </div>
    </>
  );

  if (embedded) {
    return (
      <div className="club-rules-page club-rules-page--embedded" data-testid="club-rules-page">
        {rulesMain}
      </div>
    );
  }

  return (
    <Page>
      <Container className="mt-1 club-rules-page" data-testid="club-rules-page">
        {rulesMain}
      </Container>
    </Page>
  );
}

function toIntOrNull(v) {
  if (v === '' || v === undefined || v === null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function toFloatOrNull(v) {
  if (v === '' || v === undefined || v === null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}


